import moment from "moment-timezone";
import "moment-timezone";
import { environment } from "../../../../environments/environment";

/**
 * Return the current water year in said timezone
 */
const getCurrentWaterYearBeginning = (now = today()) => {
  const year = now.month() >= 9 ? now.year() : now.year() - 1; // month 9 = October
  return moment.tz(`${year}-10-01`, environment.waterYearTimezone);
};

export const today = () => moment.tz(environment.waterYearTimezone);
let rolloverHeld = false;

export const configureWaterYearHold = (held: boolean): void => {
  if (typeof held !== "boolean") {
    throw new Error("Invalid water-year configuration from backend");
  }
  rolloverHeld = held;
};

export const currentDate = () =>
  (rolloverHeld ?
    moment.tz("2026-10-02", environment.waterYearTimezone)
  : today()
  ).subtract(2, "days");

// Active field data rolls over when October 1 ET becomes available (October 3).
// Weather alone follows the calendar water year.
export const startDateEtWaterYear = () =>
  getCurrentWaterYearBeginning(currentDate());

export const startDateCalendarWaterYear = () => getCurrentWaterYearBeginning();
export const startDateWaterYear = () => startDateEtWaterYear();
export const endDateWaterYear = () =>
  startDateWaterYear().add(1, "year").subtract(1, "day");

export const startDateWaterYearPast = () =>
  startDateWaterYear().subtract(1, "year");
export const endDateWaterYearPast = () =>
  startDateWaterYear().subtract(1, "day");
export const currentDatePast = () => endDateWaterYearPast();

/**
 * Generate Date List (string) up to current date of water year.
 * @returns dateArray
 */
export const generateWaterYearDateListPast = (): string[] => {
  // Create dateArray
  const dateArray = [];
  // Define the start date and end date
  let startDate = startDateWaterYearPast();
  const pastNow = currentDatePast();
  const pastEnd = endDateWaterYearPast();
  let endDate = pastNow.isBefore(pastEnd) ? pastNow : pastEnd;
  // Push to the dateArray day by day then return
  while (startDate <= endDate) {
    dateArray.push(startDate.format("YYYY-MM-DD"));
    startDate.add(1, "days");
  }
  return dateArray;
};

/**
 * Get the days number from the current input date to the next month's first day
 */
export const getDaysCountToNextMonthPast = (dateStr: string): number => {
  const momentDate = moment.tz(
    dateStr,
    "YYYY-MM-DD",
    environment.waterYearTimezone,
  );
  // Get the next month's first date
  const nextMonthFirstDay = momentDate
    .clone()
    .add(1, "months")
    .startOf("month");
  // Check whether the nextMonthFirstDay is less than the current Date
  if (nextMonthFirstDay <= currentDatePast()) {
    // Calculate the difference in days
    const diffDays = nextMonthFirstDay.diff(momentDate, "days");
    // Check
    return diffDays;
  } else {
    return 0;
  }
};

/**
 * Get the days number from the current input date to the previous month's first day
 */
export const getDaysCountToPreviousMonthPast = (dateStr: string): number => {
  const momentDate = moment.tz(
    dateStr,
    "YYYY-MM-DD",
    environment.waterYearTimezone,
  );
  // Get the next month's first date
  const prevMonthFirstDay = momentDate
    .clone()
    .subtract(1, "months")
    .startOf("month");
  // Check whether the nextMonthFirstDay is less than the current Date
  let startDate = startDateWaterYearPast();
  if (prevMonthFirstDay >= startDate) {
    // Calculate the difference in days
    const diffDays = momentDate.diff(prevMonthFirstDay, "days");
    // Check
    return diffDays;
  } else {
    return 0;
  }
};

/**
 * Generate Date List (string) up to current date of water year.
 * @returns dateArray
 */
export const generateWaterYearDateList = (): string[] => {
  // Create dateArray
  const dateArray = [];
  // Define the start date and end date
  const endDate = currentDate();
  const startDate = getCurrentWaterYearBeginning(endDate);
  // Push to the dateArray day by day then return
  while (startDate <= endDate) {
    dateArray.push(startDate.format("YYYY-MM-DD"));
    startDate.add(1, "days");
  }
  return dateArray;
};

/**
 * Get the days number from the current input date to the next month's first day
 */
export const getDaysCountToNextMonth = (dateStr: string): number => {
  const momentDate = moment.tz(
    dateStr,
    "YYYY-MM-DD",
    environment.waterYearTimezone,
  );
  // Get the next month's first date
  const nextMonthFirstDay = momentDate
    .clone()
    .add(1, "months")
    .startOf("month");
  // Check whether the nextMonthFirstDay is less than the current Date
  if (nextMonthFirstDay <= currentDate()) {
    // Calculate the difference in days
    const diffDays = nextMonthFirstDay.diff(momentDate, "days");
    // Check
    return diffDays;
  } else {
    return 0;
  }
};

/**
 * Get the days number from the current input date to the previous month's first day
 */
export const getDaysCountToPreviousMonth = (dateStr: string): number => {
  const momentDate = moment.tz(
    dateStr,
    "YYYY-MM-DD",
    environment.waterYearTimezone,
  );
  // Get the next month's first date
  const prevMonthFirstDay = momentDate
    .clone()
    .subtract(1, "months")
    .startOf("month");
  // Check whether the nextMonthFirstDay is less than the current Date
  const startDate = startDateEtWaterYear();
  if (prevMonthFirstDay >= startDate) {
    // Calculate the difference in days
    const diffDays = momentDate.diff(prevMonthFirstDay, "days");
    // Check
    return diffDays;
  } else {
    return 0;
  }
};
